import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '@env/environment';

export type SidebarDisplay = 'Shown' | 'Hidden';
export type SidebarItemType = 'MENU' | 'QUICK_LINK';
export type SidebarMenuLevel = 'Primary' | 'Secondary' | 'Tertiary';
export type SidebarUnavailableReason =
  | 'MENU_NOT_AVAILABLE'
  | 'VIEW_PERMISSION_REVOKED'
  | 'ANCESTOR_PERMISSION_REVOKED'
  | 'NOT_ALLOCATED_TO_TENANT';

export interface SidebarApiResponse<T> {
  status: number;
  message: string;
  data: T;
}

export interface SidebarSavedItem {
  id: number;
  uuid: string;
  item_type: SidebarItemType;
  item_display_order: number;
  sidebar_display: SidebarDisplay;
  menu_id: number | null;
  menu_level: SidebarMenuLevel | null;
  quick_link_name: string | null;
  quick_link_url: string | null;
}

export interface SidebarSavedSection {
  id: number;
  uuid: string;
  section_name: string;
  section_display_order: number;
  sidebar_display: SidebarDisplay;
  items: SidebarSavedItem[];
}

export interface SidebarEffectiveItem extends SidebarSavedItem {
  is_available: boolean;
  unavailable_reason: SidebarUnavailableReason | null;
  menu_name: string | null;
  navigation_url: string | null;
  current_menu_level: SidebarMenuLevel | null;
}

export interface SidebarEffectiveSection {
  id: number;
  uuid: string;
  section_name: string;
  section_display_order: number;
  sidebar_display: SidebarDisplay;
  items: SidebarEffectiveItem[];
}

export interface SidebarAvailableMenu {
  menu_id: number;
  menu_name: string;
  navigation_url: string | null;
  menu_level: SidebarMenuLevel;
  icon: string | null;
  sort_order: number | null;
}

export interface SidebarItemInput {
  item_type: SidebarItemType;
  item_display_order?: number | null;
  sidebar_display?: SidebarDisplay;
  menu_id?: number | null;
  menu_level?: SidebarMenuLevel | null;
  quick_link_name?: string | null;
  quick_link_url?: string | null;
}

export interface SidebarSectionInput {
  section_name: string;
  section_display_order?: number | null;
  sidebar_display?: SidebarDisplay;
  items?: SidebarItemInput[];
}

export interface SidebarFieldConstraint {
  field: string;
  message: string;
}

export function normaliseSidebarFieldPath(field: string): string {
  return String(field ?? '')
    .replace(/\[(\d+)\]/g, '.$1')
    .replace(/^\.+|\.+$/g, '')
    .replace(/\s+/g, '');
}

export function extractSidebarConstraints(
  failure: unknown,
): { message: string | null; constraints: SidebarFieldConstraint[] } {
  const body = (failure as { error?: unknown } | null)?.error ?? failure;
  if (!body || typeof body !== 'object') {
    return { message: null, constraints: [] };
  }

  const candidate = body as { message?: unknown; constraints?: unknown };
  const raw = Array.isArray(candidate.constraints) ? candidate.constraints : [];
  const constraints: SidebarFieldConstraint[] = [];

  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const { field, message } = entry as { field?: unknown; message?: unknown };
    if (typeof field !== 'string' || typeof message !== 'string') continue;
    constraints.push({ field: normaliseSidebarFieldPath(field), message });
  }

  return {
    message: typeof candidate.message === 'string' ? candidate.message : null,
    constraints,
  };
}

@Injectable({ providedIn: 'root' })
export class MySidebarService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiEndpoint}my-sidebar`;

  getSaved(): Observable<SidebarApiResponse<{ sections: SidebarSavedSection[]; meta: { include_hidden: boolean } }>> {
    return this.http.get<SidebarApiResponse<{ sections: SidebarSavedSection[]; meta: { include_hidden: boolean } }>>(this.base);
  }

  getEffective(
    includeHidden = false,
  ): Observable<
    SidebarApiResponse<{ sections: SidebarEffectiveSection[]; meta: { include_hidden: boolean; revoked_item_count: number } }>
  > {
    const params = new HttpParams().set('include_hidden', String(includeHidden));
    return this.http.get<
      SidebarApiResponse<{ sections: SidebarEffectiveSection[]; meta: { include_hidden: boolean; revoked_item_count: number } }>
    >(`${this.base}/effective`, { params });
  }

  getAvailableMenus(): Observable<
    SidebarApiResponse<{
      menus: SidebarAvailableMenu[];
      meta: { company_id: number; group_id: number; allocation_resolved: boolean };
    }>
  > {
    return this.http.get<
      SidebarApiResponse<{
        menus: SidebarAvailableMenu[];
        meta: { company_id: number; group_id: number; allocation_resolved: boolean };
      }>
    >(`${this.base}/available-menus`);
  }

  save(sections: SidebarSectionInput[]): Observable<
    SidebarApiResponse<{ sections: SidebarSavedSection[]; meta: { section_count: number; item_count: number } }>
  > {
    return this.http.put<
      SidebarApiResponse<{ sections: SidebarSavedSection[]; meta: { section_count: number; item_count: number } }>
    >(this.base, { sections });
  }

  reset(): Observable<SidebarApiResponse<{ deleted_sections: number; deleted_items: number }>> {
    return this.http.delete<SidebarApiResponse<{ deleted_sections: number; deleted_items: number }>>(this.base);
  }
}
